import { useMemo } from 'react';
import type {
  TableDropdownOption,
  TableStatusOption,
} from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { SuiteListItemDto, SuiteRunStatus } from '@flowqa/shared';

type CountMap = Partial<Record<string, number>>;

type SuitesPageFilterOptionCountArgs = {
  enabled: boolean;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  search: string;
  selectedStatuses: SuiteRunStatus[];
  selectedSuites: string[];
  selectedTags: string[];
  startDay: string | null;
  endDay: string | null;
  suiteOptions?: TableDropdownOption[];
  statusOptions?: Array<TableStatusOption<SuiteRunStatus>>;
  tagOptions?: TableDropdownOption[];
};

type CountedSuitesPageFilterOptions = {
  countedSuiteOptions: TableDropdownOption[];
  countedStatusOptions: Array<TableStatusOption<SuiteRunStatus>>;
  countedTagOptions: TableDropdownOption[];
};

const sumStatusCounts = (counts: Partial<Record<string, number>> | undefined): number =>
  Object.values(counts ?? {}).reduce<number>((sum, value) => {
    if (value === undefined) return sum;
    return sum + value;
  }, 0);

const sumExecutionStatusCounts = (counts: Partial<Record<string, number>> | undefined): number => {
  const total = counts?.total;
  if (typeof total === 'number') return total;
  return sumStatusCounts(counts);
};

export const buildSuiteExecutionCountMap = (suites: readonly SuiteListItemDto[]): CountMap =>
  Object.fromEntries(
    suites.map((suite) => [suite.id, sumExecutionStatusCounts(suite.executionStatusCounts)]),
  ) as CountMap;

export const buildSuiteStatusCountMap = (suites: readonly SuiteListItemDto[]): CountMap => {
  const counts: CountMap = {};
  suites.forEach((suite) => {
    Object.entries(suite.executionStatusCounts ?? {}).forEach(([status, value]) => {
      if (status === 'total') return;
      counts[status] = (counts[status] ?? 0) + value;
    });
  });
  return counts;
};

export const buildSuiteTagExecutionCountMap = (suites: readonly SuiteListItemDto[]): CountMap => {
  const counts: CountMap = {};
  suites.forEach((suite) => {
    const suiteCount = sumExecutionStatusCounts(suite.executionStatusCounts);
    (suite.tags ?? []).forEach((tag) => {
      counts[tag] = (counts[tag] ?? 0) + suiteCount;
    });
  });
  return counts;
};

const applyCountsToOptions = (
  options: readonly TableDropdownOption[],
  counts: CountMap | undefined,
): TableDropdownOption[] => {
  if (counts === undefined) {
    return options.map((option) => ({ ...option }));
  }
  return options.map((option) => ({
    ...option,
    count: counts[option.value] ?? 0,
  }));
};

const applyCountsToStatusOptions = (
  options: ReadonlyArray<TableStatusOption<SuiteRunStatus>>,
  counts: CountMap | undefined,
): Array<TableStatusOption<SuiteRunStatus>> => {
  if (counts === undefined) {
    return options.map((option) => ({ ...option }));
  }
  const hasErroredOption = options.some((option) => option.value === 'errored');
  return options.map((option) => {
    const groupedErroredCount =
      option.value === 'failed' && !hasErroredOption ? (counts.errored ?? 0) : 0;
    return {
      ...option,
      count: (counts[option.value] ?? 0) + groupedErroredCount,
    };
  });
};

export const useSuitesPageFilterOptionCounts = ({
  suiteOptions = [],
  statusOptions = [],
  tagOptions = [],
}: SuitesPageFilterOptionCountArgs): CountedSuitesPageFilterOptions => {
  return useMemo(
    () => ({
      countedSuiteOptions: applyCountsToOptions(suiteOptions, undefined),
      countedStatusOptions: applyCountsToStatusOptions(statusOptions, undefined),
      countedTagOptions: applyCountsToOptions(tagOptions, undefined),
    }),
    [statusOptions, suiteOptions, tagOptions],
  );
};
